import { AfterViewChecked, Component, ElementRef, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { ComponentBaseComponent } from '../component-base/component-base.component';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { TranslateService } from '@app/core/services/translate.service';

@Component({
    selector: 'tt-overflow-menu',
    templateUrl: './overflow-menu.component.html',
    styleUrls: ['./overflow-menu.component.css'],
})
export class OverflowMenuComponent extends ComponentBaseComponent implements OnChanges, AfterViewChecked, OnDestroy {
    @ViewChild('triggerButton')
    private triggerButton?: ElementRef<HTMLButtonElement>;

    @ViewChild('menu')
    private menu?: ElementRef<HTMLUListElement>;

    @Input()
    public ttText: string = '';

    public text = '';

    @Input()
    public set ttTranslateText(value: BooleanInput) {
        this._translateText = coerceBooleanProperty(value);
    }
    public get ttTranslateText(): boolean {
        return this._translateText;
    }
    private _translateText = true;

    @Input()
    public ttTooltipText: string = '';

    public tooltipText = '';

    @Input()
    public set ttTranslateTooltipText(value: BooleanInput) {
        this._translateTooltipText = coerceBooleanProperty(value);
    }
    public get ttTranslateTooltipText(): boolean {
        return this._translateTooltipText;
    }
    private _translateTooltipText = true;

    /**
     * Icon to display for the overflow menu, default is `'fa-ellipsis-v'`.
     */
    @Input()
    ttIcon?: string;

    @Input()
    ttClasses: string = '';

    @Input()
    ttMenuClasses: string = '';

    @Input()
    ttType?: 'success' | 'primary' | 'secondary' | 'danger' | 'warning' | 'link' | 'ghost-danger' | 'icon' | null = null;

    @Input()
    ttPosition: 'over' | 'under' = 'under';

    @Input()
    public set ttFlipped(value: BooleanInput) {
        this._flipped = coerceBooleanProperty(value);
    }
    public get ttFlipped(): boolean {
        return this._flipped;
    }
    private _flipped = false;

    /**
     * Whether the overflow menu is open or not.
     */
    open = false;

    /**
     * Id of elements in the overflow menu.
     */
    id = {
        button: crypto.randomUUID(),
        menu: crypto.randomUUID(),
    };

    private menuAttachedToBody = false;
    private removeWindowResizeListener?: () => void;
    private removeWindowScrollListener?: () => void;
    private removeDocumentScrollListener?: () => void;

    constructor(
        private translate: TranslateService,
        private renderer: Renderer2,
    ) {
        super();
    }

    private async setText() {
        if (this.ttTranslateText && !!this.ttText) {
            this.text = await this.translate.translate(this.ttText);
        } else {
            this.text = this.ttText;
        }
    }

    private async setTooltipText() {
        if (this.ttTranslateTooltipText && !!this.ttTooltipText) {
            this.tooltipText = await this.translate.translate(this.ttTooltipText);
        } else {
            this.tooltipText = this.ttTooltipText;
        }
    }

    /**
     * Toggled the open state of the overflow menu.
     */
    toggleMenu() {
        this.open ? this.closeMenu() : this.openMenu();
    }

    openMenu() {
        this.open = true;
    }

    closeMenu() {
        this.open = false;
        this.menuAttachedToBody = false;
        this.removePositionListeners();
    }

    onMenuClick(event: MouseEvent) {
        event.stopPropagation();
        this.closeMenu();
    }

    ngAfterViewChecked(): void {
        if (!this.open || !this.menu?.nativeElement) return;

        if (!this.menuAttachedToBody) {
            document.body.appendChild(this.menu.nativeElement);
            this.menuAttachedToBody = true;
            this.addPositionListeners();
        }

        this.positionMenu();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.['ttTranslateText']) {
            this.ttTranslateText = changes['ttTranslateText'].currentValue;
        }

        if (changes?.['ttTranslateTooltipText']) {
            this.ttTranslateTooltipText = changes['ttTranslateTooltipText'].currentValue;
        }

        if (changes?.['ttText']) {
            this.setText();
        }

        if (changes?.['ttTooltipText']) {
            this.setTooltipText();
        }
    }

    ngOnDestroy(): void {
        this.removePositionListeners();
        this.menu?.nativeElement.remove();
    }

    private addPositionListeners() {
        this.removePositionListeners();

        this.removeWindowResizeListener = this.renderer.listen('window', 'resize', () => this.positionMenu());
        this.removeWindowScrollListener = this.renderer.listen('window', 'scroll', () => this.positionMenu());
        document.addEventListener('scroll', this.positionMenu, true);
        this.removeDocumentScrollListener = () => document.removeEventListener('scroll', this.positionMenu, true);
    }

    private removePositionListeners() {
        this.removeWindowResizeListener?.();
        this.removeWindowScrollListener?.();
        this.removeDocumentScrollListener?.();
        this.removeWindowResizeListener = undefined;
        this.removeWindowScrollListener = undefined;
        this.removeDocumentScrollListener = undefined;
    }

    private positionMenu = () => {
        const triggerElement = this.triggerButton?.nativeElement;
        const menuElement = this.menu?.nativeElement;

        if (!triggerElement || !menuElement) return;

        const triggerRect = triggerElement.getBoundingClientRect();
        const menuRect = menuElement.getBoundingClientRect();
        const top = this.ttPosition === 'over' ? triggerRect.top - menuRect.height : triggerRect.bottom;
        const left = this.ttFlipped ? triggerRect.left : triggerRect.right - menuRect.width;

        const maxTop = Math.max(0, window.innerHeight - menuRect.height);
        const maxLeft = Math.max(0, window.innerWidth - menuRect.width);

        this.renderer.setStyle(menuElement, 'top', `${Math.min(Math.max(0, top), maxTop)}px`);
        this.renderer.setStyle(menuElement, 'left', `${Math.min(Math.max(0, left), maxLeft)}px`);
    };
}
